/**
 * The raised attention flag: title, reason and — when the analysis marked one —
 * the key moment the flag stands on (#2002). Placed by the panel: inside the
 * score card when the call has a score, on a card of its own when it has none
 * (#2020).
 */

import { useTranslation } from 'react-i18next';
import type { XMLAnalysisResult } from '../../AnalysisPanel';
import { describeKeyMoment } from './key-moment-view';
import { KeyMomentTime } from './KeyMomentTime';

interface AttentionFlagBlockProps {
  attentionReason?: string;
  attentionMoment?: NonNullable<XMLAnalysisResult['summary']>['attentionMoment'];
  /** Record duration — hides a hallucinated out-of-range timecode. */
  durationSeconds?: number;
  onPlaySegment?: (startSeconds: number, durationSeconds?: number) => void;
}

export function AttentionFlagBlock({
  attentionReason,
  attentionMoment,
  durationSeconds,
  onPlaySegment,
}: AttentionFlagBlockProps) {
  const { t } = useTranslation('records');
  const moment = attentionMoment
    ? describeKeyMoment(attentionMoment, t, durationSeconds)
    : null;

  return (
    <div className="rounded bg-warning-dim px-3 py-2 text-xs">
      <span className="font-semibold text-warning-fg">
        {t('records.analysisPanel.attentionRequired')}
      </span>
      {attentionReason && <p className="mt-0.5 text-warning-fg">{attentionReason}</p>}
      {moment && (
        <div className="mt-2">
          <div className="text-micro font-semibold uppercase tracking-widest text-warning-fg">
            {t('records.analysisPanel.attentionMoment', 'Moment in the conversation')}
          </div>
          <div className="mt-1 flex items-start gap-2">
            {moment.time && (
              <KeyMomentTime
                time={moment.time}
                startSeconds={moment.startSeconds}
                onPlaySegment={onPlaySegment}
                className="text-warning-fg hover:text-warning-fg"
              />
            )}
            <span className="flex-1 text-warning-fg">{moment.text}</span>
          </div>
        </div>
      )}
    </div>
  );
}
